import {
  type ComponentProps, type FC, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { mapAreaBorder } from '~/_shared/constants/mapObjects/mapAreaBorder.constants';
import { MapAreaColors } from '~/_shared/constants/mapObjects/mapAreaColors.constants';
import { getValueOfAnimatableColorProperty } from '~/_shared/types/animation/animatableProperty.helpers';
import type { LatLng } from '~/_shared/types/latLng';
import {
  type GroupRadiusProximity, type IndividualRadiusProximity, isGroupRadius,
} from '~/_shared/types/proximity/proximity.types';
import type { SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import {
  registerAnimationCallback, removeAnimationCallback,
} from '~/_shared/utils/colorAnimationTicker/colorAnimationTicker';
import { convertColorToWebGLColor } from '~/_shared/utils/colors/colors.helpers';
import { memoizeOne } from '~/_shared/utils/memoize/memoize';
import { convertUnitToKilometers } from '~/_shared/utils/unitSystem/unitSystem.helpers';
import { MapCircleComponent } from '~/map/map/mapObjects/mapCircle/mapCircle.component';
import type { MapCircleMetricInstance } from '~/map/map/mapObjects/mapCircle/mapCircleModel';
import { MapLabelComponent } from '~/map/map/mapObjects/mapLabel/mapLabel.component';
import type { MapObjectZIndex } from '~/map/map/mapObjects/mapObject.types';
import {
  createProximityDistanceLabelWithCalloutVisuals, getCircleLabelBoundingBox,
} from '~/map/map/proximity/proximity.utils';
import { proximitySubEntities } from '~/map/zIndexes/useIndividualOrDriveTimeProximityZIndex.hook';
import {
  calculateZIndex, ZIndexedEntity,
} from '~/map/zIndexes/zIndexRanges';
import { applyIndividualDetailsToProximity } from '~/proximity/proximity.helpers';
import { MapCircleMetricContainer } from '../mapObjects/mapCircle/mapCircleMetric.container';

const getActiveProximityHighlightZIndex = memoizeOne(() => ({
  // calculateZIndex needs to be initialized before being called.
  // This is a way to make the zIndex object stable and create it after the init.
  circleArea: calculateZIndex(proximitySubEntities.indexOf('entity'), ZIndexedEntity.ProximityCircleHighlight),
  outlineCircle: calculateZIndex(proximitySubEntities.indexOf('outline'), ZIndexedEntity.ProximityCircleHighlight),
  labelText: calculateZIndex(proximitySubEntities.indexOf('labelText'), ZIndexedEntity.ProximityCircleHighlight),
  labelCallout: calculateZIndex(proximitySubEntities.indexOf('label'), ZIndexedEntity.ProximityCircleHighlight),
} satisfies Partial<MapObjectZIndex> as MapObjectZIndex));

const { borderColor: alternateBorderColor, fillColor: alternateFillColor } = MapAreaColors.active;

type ActiveProximityHighlightProps = {
  readonly proximity: GroupRadiusProximity | IndividualRadiusProximity;
  readonly coordinates: LatLng;
  readonly rowId?: SpreadsheetRowId;
};

export const ActiveProximityHighlightContainer: FC<ActiveProximityHighlightProps> = ({
  proximity,
  coordinates,
  rowId,
}) => {
  const proximityWithIndividualDetails = isGroupRadius(proximity) ?
    applyIndividualDetailsToProximity(proximity, rowId) :
    proximity;

  const proximityStyleRef = useRef(proximityWithIndividualDetails.styles);
  proximityStyleRef.current = proximityWithIndividualDetails.styles;

  const [currentActiveBorderColor, setCurrentActiveBorderColor] = useState<WebglColor>(
    convertColorToWebGLColor(alternateBorderColor, 1),
  );
  const [currentActiveFillColor, setCurrentActiveFillColor] = useState<WebglColor>(
    convertColorToWebGLColor(alternateFillColor, proximityWithIndividualDetails.styles.fillOpacity),
  );

  const computeActiveBorderColor = useCallback((animationPercent: number) => {
    const borderColorAnimated = getValueOfAnimatableColorProperty({
      value: '',
      animation: { from: alternateBorderColor, to: proximityStyleRef.current.borderColor },
    }, animationPercent);
    setCurrentActiveBorderColor(convertColorToWebGLColor(borderColorAnimated, 1));

    const fillColorAnimated = getValueOfAnimatableColorProperty({
      value: '',
      animation: { from: alternateFillColor, to: proximityStyleRef.current.color },
    }, animationPercent);
    setCurrentActiveFillColor(convertColorToWebGLColor(fillColorAnimated, proximityStyleRef.current.fillOpacity));
  }, []);

  useEffect(() => {
    registerAnimationCallback(computeActiveBorderColor);

    return () => {
      removeAnimationCallback(computeActiveBorderColor);
    };
  }, [computeActiveBorderColor]);

  const borderWidth = Math.max(proximityWithIndividualDetails.styles.borderWidth + 2, mapAreaBorder.active.minWidth);

  const highlightCircle: MapCircleMetricInstance = useMemo(() => {
    return {
      ...coordinates,
      radius: convertUnitToKilometers(
        proximityWithIndividualDetails.data.radius,
        proximityWithIndividualDetails.data.unit,
      ),
      units: 'metric',
      id: `activeHighlight_${rowId?.rowId ?? proximityWithIndividualDetails.id}`,
    };
  },
  [coordinates, proximityWithIndividualDetails.data.radius, proximityWithIndividualDetails.data.unit, proximityWithIndividualDetails.id, rowId?.rowId]);

  const visuals = useMemo(() => {
    return {
      circleArea: {
        autoScale: true,
        borderColor: currentActiveBorderColor,
        borderWidth,
        fillColor: currentActiveFillColor,
        interactive: false,
        staticSize: false,
      },
    } as const;
  }, [borderWidth, currentActiveBorderColor, currentActiveFillColor]);

  const renderCircle: ComponentProps<typeof MapCircleMetricContainer>['renderCircle'] = useCallback(
    (id: string, circle: MapCircleMetricInstance) => {
      return (
        <MapCircleComponent
          key={id}
          circle={circle}
          visuals={visuals}
        />
      );
    }, [visuals]);

  const renderDistanceLabel: ComponentProps<typeof MapCircleMetricContainer>['renderCenter'] = useCallback(
    (id: string, latLng: LatLng) => {

      const labelBounds = getCircleLabelBoundingBox(
        latLng,
        proximityWithIndividualDetails.data.radius,
        proximityWithIndividualDetails.data.unit,
      );

      return (
        <MapLabelComponent
          label={{
            id,
            ...latLng,
          }}
          visuals={createProximityDistanceLabelWithCalloutVisuals(proximityWithIndividualDetails, labelBounds)}
        />
      );
    },
    [proximityWithIndividualDetails]);

  return (
    <MapCircleMetricContainer
      zIndex={getActiveProximityHighlightZIndex()}
      circle={highlightCircle}
      renderCircle={renderCircle}
      renderCenter={renderDistanceLabel}
    />
  );
};
